import { getAuctionResult } from "@/fetch/getAuctionResult";
import { datadogLogs } from "@datadog/browser-logs";
// import logger from "@/utils/logger";

export type BannerAuctionType = "category" | "landing" | "searchQuery";

export type TopsortBannerConfig = {
  header: string;
  inline: string;
  auctionType: BannerAuctionType;
  filter?: string;
};

export type WinnerResult = {
  found: boolean;
  device: "desktop" | "mobile";
  winner?: Winner;
};

export const createTopsortBannerConfig = (
  auctionType: BannerAuctionType,
  header: string,
  inline: string,
  filter?: string,
): TopsortBannerConfig | undefined => {
  if (auctionType === "category" || auctionType === "searchQuery") {
    if (!filter) {
      return undefined;
    }
  }

  return {
    header,
    inline,
    auctionType,
    filter,
  };
};

export const isManufacturerTsid = (sponsorId: string) => {
  return new RegExp(/(mfr\d+)/).test(sponsorId);
};

export const getBannerLink = (winner: Winner | undefined | null): string | undefined => {
  if (winner) {
    if (winner.type === "product") {
      const vendorParam = "?tsid=" + winner.vendorId;
      return "/ec/-d-" + winner.id + vendorParam;
    }
    if (winner.type === "url") {
      let givenUrl = winner.id;
      if (givenUrl.toLowerCase().startsWith("https://") || givenUrl.toLowerCase().startsWith("http://")) {
        return winner.id;
      } else {
        return "https://" + winner.id;
      }
    }
  }
  return undefined;
};

export const fetchBanner = async (
  auctionType: BannerAuctionType,
  slotId: string,
  apiKey: string,
  device: "desktop" | "mobile" = "desktop",
  filter?: string,
): Promise<WinnerResult> => {
  try {
    let auctionResult: AuctionResponse | null = null;

    if (auctionType === "landing") {
      auctionResult = await getAuctionResult([slotId], device, apiKey);
    } else if (auctionType === "searchQuery") {
      auctionResult = await getAuctionResult([slotId], device, apiKey, auctionType, filter);
    } else if (auctionType === "category") {
      auctionResult = await getAuctionResult([slotId], device, apiKey, auctionType, filter);
    }

    if (auctionResult?.results.length === 1) {
      const auction: Auction = auctionResult.results[0];

      if (auction.error) {
        console.error("Topsort Error Page " + slotId + "Auction Error " + auction.error);
        return {
          found: false,
          device: device,
        };
      }

      if (auction.winners.length === 0) {
        return {
          found: false,
          device: device,
        };
      }

      logBannerWinner(slotId, auction.winners[0]);

      return { found: true, device: device, winner: auction.winners[0] };
    } else {
      console.error(
        "Topsort Error Page " + slotId + " does not match auction result length " + auctionResult?.results.length,
      );
      return { found: false, device: device };
    }
  } catch (error: any) {
    console.error("Topsort Error: ", error);
    return { found: false, device: device };
  }
};

export const PRODUCT_PAGE_BANNER_SLOT_ID = "product-details-pages";

// Unlike fetchBanner, this doesn't log the winner. The product page logs it once the banner has been seen.
export const fetchProductPageBanner = async (
  categoryIds: string[],
  apiKey: string,
  device: "desktop" | "mobile",
  signal?: AbortSignal,
): Promise<Winner | null> => {
  const auctionResult = await getAuctionResult(
    [PRODUCT_PAGE_BANNER_SLOT_ID],
    device,
    apiKey,
    "category",
    categoryIds,
    signal,
  );
  const auction = auctionResult?.results?.[0];
  if (auction?.error) {
    console.error("Topsort Error Page " + PRODUCT_PAGE_BANNER_SLOT_ID + " Auction Error " + auction.error);
    return null;
  }
  return auction?.winners?.[0] ?? null;
};

export const logBannerWinner = (slotId: string, winner: Winner) => {
  datadogLogs.logger.info("Topsort auction result", {
    event: "TopsortAuctionResult",
    slotId,
    resolvedBidId: winner.resolvedBidId,
    path: typeof window !== "undefined" ? window.location.pathname : undefined,
    auctionType: "banner",
  });
};
